import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { User, Building2, Mail } from "lucide-react";
import { SYSTEM_ICON } from "../../../constants/entity-icons";
import { cn } from "../../../utils";
import { appendDeletedSuffix, DELETED_ENTITY_CLASS, isAssigneeDeleted } from "../../../utils/deleted-entity-utils";
import { resolveEntityRefDisplay } from "./suggestionNames";
import { ProposedEntityNameLabel } from "./ProposedEntityName";
import { UserName } from "../user-card/user-name";
/**
 * Renders successor entities (persons, orgFunctions, systems, external contacts)
 * as a compact list with icons. Used in both the SuccessorsSection and
 * DecisionRulesSection.
 *
 * `orgFunctions` / `systems` carry `EntityOrSuggestionRef` — populated lookups
 * (when provided) resolve to a name; unresolved suggestion refs render as a
 * pending placeholder so the row count stays accurate. Soft-deleted entities
 * render grayed out with a "(deleted)" suffix.
 */ export function SuccessorEntities({ successors, t, orgFunctionsById, systemsById, usersById, suggestionInfoById }) {
    const { persons, orgFunctions, systems, externalContacts } = successors;
    const hasEntities = (persons?.length || 0) + (orgFunctions?.length || 0) + (systems?.length || 0) + (externalContacts?.length || 0) > 0;
    if (successors.successorStatus === "NONE") {
        return /*#__PURE__*/ _jsx("span", {
            className: "text-fg-subtle italic",
            children: t("processFlow.noSuccessors")
        });
    }
    if (successors.successorStatus === "UNCLEAR") {
        return /*#__PURE__*/ _jsx("span", {
            className: "text-fg-subtle italic",
            children: t("processFlow.unclearSuccessors")
        });
    }
    if (!hasEntities) return null;
    const pendingOrgFunction = t("processFlow.pendingOrgFunction");
    const pendingSystem = t("processFlow.pendingSystem");
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-0.5",
        children: [
            persons?.map((person, idx)=>{
                // Prefer the lookup entry — backend-populated persons have deletedAt
                // stripped; lookup-sourced persons carry it either way.
                const lifecycleSource = usersById?.get(person._id) ?? person;
                const isDeleted = isAssigneeDeleted(lifecycleSource);
                return /*#__PURE__*/ _jsxs("div", {
                    className: cn("flex items-center gap-1 whitespace-nowrap", isDeleted && DELETED_ENTITY_CLASS),
                    children: [
                        /*#__PURE__*/ _jsx(User, {
                            className: "w-3 h-3 text-fg-subtle shrink-0"
                        }),
                        /*#__PURE__*/ _jsx(UserName, {
                            name: `${person.firstName} ${person.lastName}`,
                            user: lifecycleSource
                        })
                    ]
                }, person._id || idx);
            }),
            orgFunctions?.map((ref, idx)=>{
                const row = resolveEntityRefDisplay(ref, {
                    suggestionInfoById,
                    pendingLabel: pendingOrgFunction,
                    getEntity: (id)=>orgFunctionsById?.get(id),
                    t
                });
                return /*#__PURE__*/ _jsxs("div", {
                    className: cn("flex items-center gap-1 whitespace-nowrap", row.kind === "entity" && row.isDeleted && DELETED_ENTITY_CLASS),
                    children: [
                        /*#__PURE__*/ _jsx(Building2, {
                            className: "w-3 h-3 text-fg-subtle shrink-0"
                        }),
                        row.kind === "proposed" ? /*#__PURE__*/ _jsx(ProposedEntityNameLabel, {
                            name: row.label,
                            isDeclined: row.isDeclined
                        }) : /*#__PURE__*/ _jsx("span", {
                            children: row.label
                        })
                    ]
                }, `orgFunction-${ref.id || idx}`);
            }),
            systems?.map((ref, idx)=>{
                const row = resolveEntityRefDisplay(ref, {
                    suggestionInfoById,
                    pendingLabel: pendingSystem,
                    getEntity: (id)=>systemsById?.get(id),
                    t
                });
                return /*#__PURE__*/ _jsxs("div", {
                    className: cn("flex items-center gap-1 whitespace-nowrap", row.kind === "entity" && row.isDeleted && DELETED_ENTITY_CLASS),
                    children: [
                        /*#__PURE__*/ _jsx(SYSTEM_ICON, {
                            className: "w-3 h-3 text-fg-subtle shrink-0"
                        }),
                        row.kind === "proposed" ? /*#__PURE__*/ _jsx(ProposedEntityNameLabel, {
                            name: row.label,
                            isDeclined: row.isDeclined
                        }) : /*#__PURE__*/ _jsx("span", {
                            children: row.label
                        })
                    ]
                }, `system-${ref.id || idx}`);
            }),
            externalContacts?.map((contact, idx)=>{
                const isDeleted = isAssigneeDeleted(contact);
                return /*#__PURE__*/ _jsxs("div", {
                    className: cn("flex items-center gap-1 whitespace-nowrap", isDeleted && DELETED_ENTITY_CLASS),
                    children: [
                        /*#__PURE__*/ _jsx(Mail, {
                            className: "w-3 h-3 text-fg-subtle shrink-0"
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            children: appendDeletedSuffix(contact.name, isDeleted, t)
                        })
                    ]
                }, contact._id || idx);
            })
        ]
    });
}

//# sourceMappingURL=SuccessorEntities.js.map